import { Color, Label, Scene, Vector, vec } from 'excalibur';
import { FloatingScore } from '../actors/floating-score';
import { Hearts } from '../actors/hearts';
import {
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
  DOUBLES_PER_HEART,
  LOW_TIME_THRESHOLD,
  START_TIME,
} from '../utils/constants';
import { Palette } from '../utils/palette';
import { isTouchDevice } from '../utils/device';
import { HandLabel } from './label';
import { TimerBar } from './timer-bar';

export class Hud {
  private readonly scene: Scene;
  private scoreValue = -1;
  private timeValue = -1;
  private readonly scoreLabel: Label;
  private readonly timerLabel: Label;
  private readonly hintLabel: Label;
  private readonly doublesLabel: HandLabel;
  private readonly hearts: Hearts;
  private readonly bar: TimerBar;
  private scorePulse = 0;
  private time = 0;
  private compact = false;
  private compactCheck = 0;

  constructor(scene: Scene) {
    this.scene = scene;
    const scoreCaption = new HandLabel({
      name: 'hud-score-caption',
      text: 'score',
      pos: vec(40, 36),
      anchor: vec(0, 0.5),
      size: 26,
      tone: 'inkSoft',
    });
    this.scoreLabel = new HandLabel({
      name: 'hud-score',
      text: '0',
      pos: vec(40, 84),
      anchor: vec(0, 0.5),
      size: 58,
      tone: 'ink',
    });
    this.timerLabel = new HandLabel({
      name: 'hud-timer',
      text: START_TIME.toFixed(1),
      pos: vec(DESIGN_WIDTH / 2, 56),
      size: 48,
      tone: 'ink',
    });
    this.hintLabel = new HandLabel({
      name: 'hud-hint',
      text: Hud.hintText(),
      pos: vec(DESIGN_WIDTH / 2, DESIGN_HEIGHT - 56),
      size: 24,
      tone: 'inkSoft',
      maxWidth: 620,
    });
    this.hearts = new Hearts();
    this.bar = new TimerBar();
    this.doublesLabel = new HandLabel({
      name: 'hud-doubles',
      text: '',
      pos: vec(DESIGN_WIDTH - 34, 122),
      anchor: vec(1, 0.5),
      size: 19,
      tone: 'inkSoft',
    });
    scene.add(scoreCaption);
    scene.add(this.scoreLabel);
    scene.add(this.timerLabel);
    scene.add(this.hintLabel);
    scene.add(this.bar);
    scene.add(this.hearts);
    scene.add(this.doublesLabel);
    this.setTime(START_TIME);
    this.setDoubles(0);
  }

  static hintText(): string {
    return isTouchDevice() ? 'tap anywhere · hold the button to zoom' : 'hold space to zoom';
  }

  setScore(score: number): void {
    if (score === this.scoreValue) {
      return;
    }
    this.scoreValue = score;
    this.scoreLabel.text = String(score);
    this.scorePulse = 1;
  }

  setTime(seconds: number): void {
    const clamped = Math.max(0, seconds);
    const rounded = Math.round(clamped * 10) / 10;
    if (rounded !== this.timeValue) {
      this.timeValue = rounded;
      this.timerLabel.text = rounded.toFixed(1);
    }
    this.bar.ratio = clamped / START_TIME;
    this.bar.danger = clamped <= LOW_TIME_THRESHOLD;
  }

  setLives(lives: number): void {
    this.hearts.setHearts(lives);
  }

  setDoubles(doubles: number): void {
    this.doublesLabel.text = `${doubles % DOUBLES_PER_HEART}/${DOUBLES_PER_HEART} doubles for a heart`;
  }

  setReady(ready: boolean): void {
    this.hintLabel.text = ready ? 'get ready...' : Hud.hintText();
  }

  popup(text: string, pos: Vector, color: Color, size: number, rise?: number): void {
    const popup = new FloatingScore({
      name: `popup-${Math.round(pos.x)}-${Math.round(pos.y)}-${this.time.toFixed(2)}`,
      text,
      pos,
      color,
      size,
      rise,
    });
    this.scene.add(popup);
  }

  reset(): void {
    this.scoreValue = -1;
    this.timeValue = -1;
    this.scorePulse = 0;
    this.scoreLabel.scale = vec(1, 1);
    this.hintLabel.text = Hud.hintText();
    this.hearts.reset();
    this.setDoubles(0);
    this.setTime(START_TIME);
  }

  update(dt: number): void {
    this.time += dt;
    this.compactCheck += dt;
    if (this.compactCheck > 0.5) {
      this.compactCheck = 0;
      this.evaluateCompactMode();
    }
    if (this.scorePulse > 0) {
      this.scorePulse = Math.max(0, this.scorePulse - dt * 4);
      const scale = 1 + this.scorePulse * 0.22;
      this.scoreLabel.scale = vec(scale, scale);
    }
    const danger = this.bar.danger;
    const pulse = danger ? 1 + (Math.sin(this.time * 9) + 1) * 0.06 : 1;
    this.timerLabel.scale = vec(pulse, pulse);
    this.timerLabel.color = danger ? Palette.pinkDeep : Palette.ink;
  }

  private evaluateCompactMode(): void {
    const width = this.scene.engine.canvas.getBoundingClientRect().width;
    const compact = width > 0 && width < 380;
    if (compact === this.compact) {
      return;
    }
    this.compact = compact;
    this.scoreLabel.font.size = compact ? 70 : 58;
    this.timerLabel.font.size = compact ? 58 : 48;
    this.hintLabel.graphics.isVisible = !compact;
    this.doublesLabel.graphics.isVisible = !compact;
  }
}
